import { supabase } from '@/lib/supabase';
import { 
  Region, 
  CreateRegionInput, 
  UpdateRegionInput,
  RegionServiceError,
  RegionErrorCodes
} from '@/features/regions/types';

export const regionMutations = {
  /**
   * Create a new region for a provider
   */
  createRegion: async (data: CreateRegionInput): Promise<Region> => {
    try {
      // Check for duplicate name
      const { data: existingRegion, error: checkError } = await supabase
        .from('regions')
        .select('id')
        .eq('provider_id', data.provider_id)
        .eq('name', data.name)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw new RegionServiceError(
          'Error checking existing regions',
          RegionErrorCodes.CREATE_ERROR,
          checkError
        );
      }

      if (existingRegion) {
        throw new RegionServiceError(
          `Region with name "${data.name}" already exists for this provider`,
          RegionErrorCodes.DUPLICATE_NAME
        );
      }

      // Handle optional zip_code
      // If it's an empty string or null, set it to undefined
      const zipCode = data.zip_code?.trim() || undefined;

      // Create the region
      const { data: region, error } = await supabase
        .rpc('create_region', {
          p_provider_id: data.provider_id,
          p_name: data.name,
          p_zip_code: zipCode
        });

      if (error) {
        throw new RegionServiceError(
          'Failed to create region',
          RegionErrorCodes.CREATE_ERROR,
          error
        );
      }

      // Retrieve the created region
      const { data: createdRegion, error: fetchError } = await supabase
        .from('regions')
        .select('*')
        .eq('id', region)
        .single();

      if (fetchError) {
        throw new RegionServiceError(
          'Failed to fetch created region',
          RegionErrorCodes.CREATE_ERROR,
          fetchError
        );
      }

      return createdRegion as Region;
    } catch (error) {
      if (error instanceof RegionServiceError) {
        throw error;
      }

      throw new RegionServiceError(
        'Failed to create region',
        RegionErrorCodes.CREATE_ERROR,
        error
      );
    }
  },

  /**
   * Update an existing region
   */
  updateRegion: async (id: number, data: UpdateRegionInput): Promise<Region> => {
    try {
      // Check if region exists
      const { data: existingRegion, error: checkError } = await supabase
        .from('regions')
        .select('id, provider_id')
        .eq('id', id)
        .single();

      if (checkError) {
        throw new RegionServiceError(
          'Region not found',
          RegionErrorCodes.NOT_FOUND,
          checkError
        );
      }

      // Check for duplicate name
      if (data.name) {
        const { data: duplicateName, error: nameError } = await supabase
          .from('regions')
          .select('id')
          .eq('provider_id', existingRegion.provider_id || 0)
          .eq('name', data.name)
          .neq('id', id)
          .single();

        if (nameError && nameError.code !== 'PGRST116') {
          throw new RegionServiceError(
            'Error checking region name',
            RegionErrorCodes.UPDATE_ERROR,
            nameError
          );
        }

        if (duplicateName) {
          throw new RegionServiceError(
            `Region with name "${data.name}" already exists for this provider`,
            RegionErrorCodes.DUPLICATE_NAME
          );
        }
      }

      // Process zip code - handle empty strings
      const updateData = { ...data };
      if (typeof updateData.zip_code === 'string') {
        // If it's an empty string, set to undefined, otherwise trim it
        updateData.zip_code = updateData.zip_code.trim() || undefined;
      }
      
      // Handle city_coverage - convert array to string if needed
      if (Array.isArray(updateData.city_coverage)) {
        (updateData as any).city_coverage = updateData.city_coverage.join(', ');
      }

      // Update the region
      const { data: updatedRegion, error } = await supabase
        .from('regions')
        .update(updateData as any)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new RegionServiceError(
          'Failed to update region',
          RegionErrorCodes.UPDATE_ERROR,
          error
        );
      }

      return updatedRegion as Region;
    } catch (error) {
      if (error instanceof RegionServiceError) {
        throw error;
      }

      throw new RegionServiceError(
        'Failed to update region',
        RegionErrorCodes.UPDATE_ERROR,
        error
      );
    }
  },

  /**
   * Delete a region (if it has no versions)
   */
  deleteRegion: async (id: number): Promise<void> => {
    try {
      // Use the database function that checks version counts
      const { error } = await supabase
        .rpc('delete_region', {
          p_region_id: id
        });

      if (error) {
        if (error.message.includes('associated versions')) {
          throw new RegionServiceError(
            'Cannot delete region with associated versions',
            RegionErrorCodes.HAS_VERSIONS,
            error
          );
        }

        throw new RegionServiceError(
          'Failed to delete region',
          RegionErrorCodes.DELETE_ERROR,
          error
        );
      }
    } catch (error) {
      if (error instanceof RegionServiceError) {
        throw error;
      }

      throw new RegionServiceError(
        'Failed to delete region',
        RegionErrorCodes.DELETE_ERROR,
        error
      );
    }
  }
};
